import {
  Wrap,
  Link,
  Text,
} from '@chakra-ui/react';
import {
  usePavementCondition,
  useSurfaceCondition,
} from '@src/modules/performance/context/reports-api-hooks';
import { selectedDatabaseIdState, currentDatabaseSelector } from '@transport-insights/uikit';
import { useRecoilValue } from 'recoil';
import GroupedRegionMessage from '@src/modules/performance/components/GroupedRegionMessage';
import LineRangeAreaChart from '@src/modules/performance/components/charts/LineRangeAreaChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Road Condition (Sealed Roads)',
  content: [
    {
      title: 'Pavement condition',
      description:
  <>
    <Text fontSize="inherit" mb={2}>PII is a combined index of the pavement faults in the sealed road surface.</Text>
    <Text fontSize="inherit" mb={2}>The results are out of 100 (%) with the higher the number, the better the ride quality or condition.</Text>
    <Text fontSize="inherit" mb={2}>The average result for the peer group is shown in a lighter shade of the same colour to provide context.</Text>
  </>,
      source: null,
    },
    {
      title: 'Surface condition',
      description:
  <Text fontSize="inherit" mb={2}>CI is a single index summarising surface condition based on visually measured condition defects.</Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>NZTA Data and Tools, road condition</Link>
  </Text>,
    },
  ],
};

function RoadConditionSealedRoads() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data: pavementCondition, isLoading: isLoadingPavementCondition } = usePavementCondition();
  const { data: surfaceCondition, isLoading: isLoadingSurfaceCondition } = useSurfaceCondition();
  const isLoading = isLoadingPavementCondition || isLoadingSurfaceCondition;
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isNational) {
    chartFilters = ['Peer', 'Percentile'];
  } else if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }

  const lockYear = pavementCondition?.lockYear || surfaceCondition?.lockYear;

  const pavementConditionNoData = pavementCondition?.map((item) => item.Value).every((val) => val === null);
  const surfaceConditionNoData = surfaceCondition?.map((item) => item.Value).every((val) => val === null);

  // Pavement Condition Chart
  const pavementConditionYears = [...new Set(pavementCondition?.map((item) => item.Year) || [])];
  const pavementConditionData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: pavementConditionNoData ? []
      : pavementConditionYears.map((year) => {
        const { Value } = pavementCondition?.find((item) => item.Year === year) || {};
        return {
          x: year,
          y: Value ? Value * 100 : null,
        };
      }),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: pavementConditionNoData ? []
      : pavementConditionYears.map((year) => {
        const { PeerGroupValue } = pavementCondition?.find((item) => item.Year === year) || {};
        return {
          x: year,
          y: PeerGroupValue ? PeerGroupValue * 100 : null,
        };
      }),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th Percentile',
    type: 'rangeArea',
    data: pavementConditionNoData ? []
      : pavementConditionYears.map((year) => {
        const { LowPercentile, HighPercentile } = pavementCondition?.find((item) => item.Year === year) || {};
        return {
          x: year,
          y: LowPercentile ? [LowPercentile * 100, HighPercentile * 100] : null,
        };
      }),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const pavementConditionChartSeries = filterChartDataByName(
    pavementConditionData,
    chartFilters,
  );

  // Surface Condition Chart
  const surfaceConditionYears = [...new Set(surfaceCondition?.map((item) => item.Year) || [])];
  const surfaceConditionData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: surfaceConditionNoData ? []
      : surfaceConditionYears.map((year) => {
        const { Value } = surfaceCondition?.find((item) => item.Year === year) || {};
        return {
          x: year,
          y: Value ? Value * 100 : null,
        };
      }),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: surfaceConditionNoData ? []
      : surfaceConditionYears.map((year) => {
        const { PeerGroupValue } = surfaceCondition?.find((item) => item.Year === year) || {};
        return {
          x: year,
          y: PeerGroupValue ? PeerGroupValue * 100 : null,
        };
      }),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th Percentile',
    type: 'rangeArea',
    data: surfaceConditionNoData ? []
      : surfaceConditionYears.map((year) => {
        const { LowPercentile, HighPercentile } = surfaceCondition?.find((item) => item.Year === year) || {};
        return {
          x: year,
          y: LowPercentile ? [LowPercentile * 100, HighPercentile * 100] : null,
        };
      }),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const surfaceConditionChartSeries = filterChartDataByName(
    surfaceConditionData,
    chartFilters,
  );
  const markers = isRegionNationalOrStateHighways ? {
    size: [3, 0],
    hover: {
      size: 6,
    },
  } : {
    size: [3, 3, 0],
    hover: {
      size: 6,
    },
  };

  const stroke = isRegionNationalOrStateHighways ? {
    curve: 'straight',
    width: [2, 0],
  } : {
    curve: 'straight',
    width: [2, 2, 0],
  };

  return (
    <ReportSection
      title="Road Condition (Sealed Roads)"
      isLoading={isLoading || !pavementCondition || !surfaceCondition}
      lockYear={lockYear}
      informationSource={informationSource}
    >
      <Wrap spacing={0} justify="center">
        <LineRangeAreaChart
          title="Pavement Condition"
          categories={pavementConditionYears}
          series={pavementConditionChartSeries}
          width="100%"
          height="280"
          markers={markers}
          stroke={stroke}
          prefix=""
          suffix="%"
          decimalPoints={1}
          yAxisDecimalPoints={1}
        />
        <LineRangeAreaChart
          title="Surface Condition"
          categories={surfaceConditionYears}
          series={surfaceConditionChartSeries}
          width="100%"
          height="280"
          markers={markers}
          stroke={stroke}
          prefix=""
          suffix="%"
          decimalPoints={1}
          yAxisDecimalPoints={1}
        />
      </Wrap>
      <GroupedRegionMessage rcaIds={[1014, 1015, 1016]} />
    </ReportSection>
  );
}

export default RoadConditionSealedRoads;
